import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/shop", label: "Shop All" },
  { to: "/collections/hd-lace", label: "HD Lace" },
  { to: "/collections/glueless", label: "Glueless" },
  { to: "/collections/curly", label: "Curly" },
  { to: "/collections/straight", label: "Straight" },
  { to: "/collections/blonde", label: "Blonde" },
  { to: "/collections/beginner-friendly", label: "Beginner Friendly" },
  { to: "/collections/best-sellers", label: "Best Sellers" },
];

export default function Navigation() {
  const { count, setDrawerOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    setSearchOpen(false);
    navigate(`/shop?q=${encodeURIComponent(search)}`);
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 border-b border-stone-line ${
        scrolled ? "bg-ivory/90 backdrop-blur-xl" : "bg-ivory"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Mobile menu */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 -ml-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu strokeWidth={1.5} className="w-5 h-5" />
          </button>

          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex-1 lg:flex-none text-center lg:text-left">
            <span className="font-serif text-2xl tracking-wide">Maison Luxe</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8 mx-auto">
            {NAV.slice(0, 6).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-luxe hover:text-gold transition-colors ${
                    isActive ? "text-charcoal" : "text-stone"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-1 lg:gap-3">
            <button
              data-testid="search-toggle-button"
              onClick={() => setSearchOpen((s) => !s)}
              className="p-2 hover:text-gold transition-colors"
              aria-label="Search"
            >
              <Search strokeWidth={1.5} className="w-5 h-5" />
            </button>
            <Link
              to={user && user.role === "admin" ? "/admin" : user ? "/account" : "/login"}
              data-testid="account-link"
              className="p-2 hover:text-gold transition-colors hidden sm:inline-flex"
              aria-label="Account"
            >
              <User strokeWidth={1.5} className="w-5 h-5" />
            </Link>
            <button
              data-testid="cart-icon-button"
              onClick={() => setDrawerOpen(true)}
              className="relative p-2 hover:text-gold transition-colors"
              aria-label="Cart"
            >
              <ShoppingBag strokeWidth={1.5} className="w-5 h-5" />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 bg-charcoal text-ivory text-[10px] font-medium w-4 h-4 rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Search overlay */}
        {searchOpen && (
          <div className="border-t border-stone-line py-4 fade-up">
            <form onSubmit={submitSearch} className="flex items-center gap-3 max-w-2xl mx-auto">
              <Search strokeWidth={1.5} className="w-5 h-5 text-stone" />
              <input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search wigs, lengths, textures…"
                autoFocus
                className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-stone py-2"
              />
              <button data-testid="search-submit-button" className="text-xs uppercase tracking-luxe text-charcoal hover:text-gold">
                Search
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-ivory fade-up">
          <div className="flex items-center justify-between p-5 border-b border-stone-line">
            <span className="font-serif text-xl">Maison Luxe</span>
            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="p-2">
              <X strokeWidth={1.5} className="w-5 h-5" />
            </button>
          </div>
          <nav className="flex flex-col p-6 gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                className="py-3 border-b border-stone-line text-sm uppercase tracking-luxe"
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/about" onClick={() => setOpen(false)} className="py-3 border-b border-stone-line text-sm uppercase tracking-luxe">About</Link>
            <Link to="/faq" onClick={() => setOpen(false)} className="py-3 border-b border-stone-line text-sm uppercase tracking-luxe">FAQ</Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="py-3 border-b border-stone-line text-sm uppercase tracking-luxe">Contact</Link>
            <Link to="/track-order" onClick={() => setOpen(false)} className="py-3 border-b border-stone-line text-sm uppercase tracking-luxe">Track Order</Link>
            {!user || user === false ? (
              <Link to="/login" onClick={() => setOpen(false)} className="py-3 text-sm uppercase tracking-luxe">Login / Register</Link>
            ) : (
              <Link to="/account" onClick={() => setOpen(false)} className="py-3 text-sm uppercase tracking-luxe">My Account</Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
